import { defineStore } from 'pinia'

import { clientConfig, type ThemeMode, type ThemeTokens } from '@/app/branding'
import { dashboardTheme } from '@/dashboard/themes/theme-6'

function storageKey() {
  return `dashboard.theme.${clientConfig.id}`
}

function isThemeMode(value: string | null): value is ThemeMode {
  return value === 'light' || value === 'dark'
}

function storedTheme(): ThemeMode | null {
  const storage = window.localStorage
  const branded = storage.getItem(storageKey())
  if (isThemeMode(branded)) {
    storage.removeItem('theme')
    return branded
  }

  const legacy = storage.getItem('theme')
  if (!isThemeMode(legacy)) return null

  storage.setItem(storageKey(), legacy)
  storage.removeItem('theme')
  return legacy
}

function applyTokens(tokens: ThemeTokens) {
  const root = document.documentElement
  const variables: Record<string, string> = {
    '--color-bg': tokens.background,
    '--color-bg-elevated': tokens.backgroundElevated,
    '--color-sidebar': tokens.sidebar,
    '--color-surface': tokens.surface,
    '--color-surface-muted': tokens.surfaceMuted,
    '--color-text': tokens.text,
    '--color-text-muted': tokens.textMuted,
    '--color-text-faint': tokens.textFaint,
    '--color-border': tokens.border,
    '--color-border-strong': tokens.borderStrong,
    '--color-primary': tokens.primary,
    '--color-primary-text': tokens.primaryText,
    '--color-primary-hover': tokens.primaryHover,
    '--color-on-primary': tokens.onPrimary,
    '--color-primary-soft': tokens.primarySoft,
    '--color-success': tokens.success,
    '--color-warning': tokens.warning,
    '--color-danger': tokens.danger,
    '--color-focus': tokens.focus,
  }
  tokens.chart.forEach((color, index) => {
    variables[`--color-chart-${index + 1}`] = color
  })
  Object.entries(variables).forEach(([name, value]) => root.style.setProperty(name, value))
}

export const useThemeStore = defineStore('theme', {
  state: () => ({ current: dashboardTheme.defaultMode as ThemeMode, booted: false }),
  getters: {
    isDark: (state) => state.current === 'dark',
    tokens: (state): ThemeTokens => dashboardTheme.tokens[state.current],
  },
  actions: {
    boot() {
      this.current = storedTheme() ?? dashboardTheme.defaultMode
      this.apply()
      this.booted = true
    },
    set(mode: ThemeMode) {
      this.current = mode
      window.localStorage.setItem(storageKey(), mode)
      window.localStorage.removeItem('theme')
      this.apply()
    },
    toggle() {
      this.set(this.current === 'dark' ? 'light' : 'dark')
    },
    apply() {
      const root = document.documentElement
      root.dataset.brand = clientConfig.id
      root.dataset.dashboardTheme = dashboardTheme.id
      root.dataset.theme = this.current
      root.classList.toggle('dark', this.current === 'dark')
      root.style.colorScheme = this.current
      applyTokens(this.tokens)
      document.querySelector<HTMLMetaElement>('meta[name="theme-color"]')?.setAttribute('content', this.tokens.background)
    },
  },
})
